import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Params } from '@angular/router';

import { Observable, Subject, pipe } from 'rxjs';
import { map, tap, shareReplay, catchError } from 'rxjs/operators';

import { CacheService } from '@library/core/services';
import { ResponseWrapper } from '@library/common/http';

import { AdminSourceSystem } from '../admin-source-system';
export { AdminSourceSystem };

import { AccountListItem, Account, AccountUser, AccountBookingConfirmation } from './admin-account';
export { AccountListItem, Account, AccountUser, AccountBookingConfirmation };

import * as _ from 'lodash';

@Injectable({
   providedIn: 'root'
})
export class AdminAccountService {

   url = 'admin/account';

   refresh$: Subject<void> = new Subject();

   constructor(
      private cache: CacheService,
      private http: HttpClient,
   ) { }

   /* uses admin/v1.0/list-account.schema.json */
   list( opts: { portal: string } | Params ): Observable<AccountListItem[]> {
      const { portal } = opts;
      if ( !portal ) { throw new Error( 'list - Missing portal from opts' ); }

      return this.cache.request<ResponseWrapper<AccountListItem>, AccountListItem[]>({
         url: `${this.url}/${portal}/list`, params: {},
         pipe: pipe(
            map( rw => rw.response.collection ),
            map( list => list.map( a => new AccountListItem( a ))),
            map( list => _.sortBy(list, ['name', 'accountNumber'])),
            shareReplay(),
         ),
         refresher: this.refresh$,
         useLambda: true,
      });
   }

   /* uses admin/v1.0/get-account.schema.json */
   account( opts: { portal: string, id: string } | Params, headers: any = null ): Observable<Account> {
      const { portal, id } = opts;
      if ( !portal ) { throw new Error( 'account - Missing portal from opts' ); }
      if ( !id ) { throw new Error( 'account - Missing id from opts' ); }

      return this.cache.request<ResponseWrapper<Account>, Account>({
         url: `${this.url}/${portal}/${id}`,
         pipe: pipe(
            map( rw => rw.response.collection[0] ),
            map( a => new Account( a )),
            shareReplay(),
         ),
         headers,
         useLambda: true,
      });
   }

   createAccount( opts: { portal: string } | Params, account: Account ) {
      const { portal } = opts;
      if ( !portal ) { throw new Error( 'saveAccount - Missing portal from opts' ); }

      return this.http.post<ResponseWrapper<{ id: number }>>( `${this.url}/${portal}`, account ).pipe(
         map( rw => rw.response.collection[0] ),
         tap( _ => this.cache.clearSet( this.url )),
      );
   }

   /* uses admin/v1.0/modify-account.schema.json */
   saveAccount( opts: { portal: string, id: string } | Params, account: Account ) {
      const { portal, id } = opts;
      if ( !portal ) { throw new Error( 'saveAccount - Missing portal from opts' ); }
      if ( !id ) { throw new Error( 'saveAccount - Missing id from opts' ); }

      const url = `${this.url}/${portal}/${id}`;

      return this.http.patch<ResponseWrapper<number>>( url, account, { params: { useLambda: true } } ).pipe(
         map( rw => rw.response.collection[0] ),
         tap( _ => this.cache.clearSet( this.url )),
      );
   }

   /* uses admin/v1.0/get-account-users.schema.json */
   users( opts: { portal: string, id: string } | Params ): Observable<AccountUser[]> {
      const { portal, id } = opts;
      if ( !portal ) { throw new Error( 'users - Missing portal from opts' ); }
      if ( !id ) { throw new Error( 'users - Missing id from opts' ); }

      return this.cache.request<ResponseWrapper<AccountUser>, AccountUser[]>({
         url: `${this.url}/${portal}/${id}/users`,
         pipe: pipe(
            map( rw => rw.response.collection ),
            map( list => list.map( a => new AccountUser( a ))),
            map( list => list.sort(( a, b ) => a.lastName < b.lastName ? 1 : -1 )),
            map( list => list.sort(( a, b) => a.firstName > b.firstName ? 1 : -1 )),
            shareReplay(),
         ),
         refresher: this.refresh$,
         useLambda: true,
      });
   }

   /* uses admin/v1.0/get-account-source-systems.schema.json */
   sourceSystems( opts: { portal: string, id: string } | Params ): Observable<AdminSourceSystem[]> {
      const { portal, id } = opts;
      if ( !portal ) { throw new Error( 'sourceSystems - Missing portal from opts' ); }
      if ( !id ) { throw new Error( 'sourceSystems - Missing id from opts' ); }

      return this.cache.request<ResponseWrapper<AdminSourceSystem>, AdminSourceSystem[]>({
         url: `${this.url}/${portal}/${id}/source-system`,
         pipe: pipe(
            map( rw => rw.response.collection ),
            map( list => list.map( a => new AdminSourceSystem( a ))),
            map( list => list.sort(( a, b) => a.sourceSystem > b.sourceSystem ? 1 : -1 )),
            shareReplay(),
         ),
         refresher: this.refresh$,
         useLambda: true,
      });
   }

   saveAccountSourceSystems( opts: { portal: string, id: string } | Params, sourceSystems: AdminSourceSystem[] ) {
      const { portal, id } = opts;
      if ( !portal ) { throw new Error( 'saveAccountSourceSystems - Missing portal from opts' ); }
      if ( !id ) { throw new Error( 'saveAccountSourceSystems - Missing id from opts' ); }

      const url = `${this.url}/${portal}/${id}/source-system`;

      return this.http.put<ResponseWrapper<boolean>>( url, sourceSystems, { params: { useLambda: true } } ).pipe(
         map( rw => rw.response.collection[0] ),
         tap( _ => this.cache.clearSet( this.url )),
         tap( _ => this.cache.clearSet( `admin/${portal}/${id}/services` )),
         catchError( e => { throw new Error( e ); }),
      );
   }

   /* uses admin/v1.0/get-account-booking-confirmation.schema.json */
   bookingConfirmation( opts: { portal: string, id: string } | Params ): Observable<AccountBookingConfirmation> {
      const { portal, id } = opts;
      if ( !portal ) { throw new Error( 'bookingConfirmation - Missing portal from opts' ); }
      if ( !id ) { throw new Error( 'bookingConfirmation - Missing id from opts' ); }

      return this.cache.request<ResponseWrapper<AccountBookingConfirmation>, AccountBookingConfirmation>({
         url: `${this.url}/${portal}/${id}/booking-confirmation`,
         pipe: pipe(
            map( rw => rw.response.collection[0] ),
            map( abc => new AccountBookingConfirmation( abc )),
            shareReplay(),
         ),
         refresher: this.refresh$,
         useLambda: true,
      });
   }

   /* uses admin/v1.0/modify-account-booking-confirmation.schema.json */
   saveBookingConfirmation( opts: { portal: string, id: string } | Params, abc: AccountBookingConfirmation ) {
      const { portal, id } = opts;
      if ( !portal ) { throw new Error( 'saveBookingConfirmation - Missing portal from opts' ); }
      if ( !id ) { throw new Error( 'saveBookingConfirmation - Missing id from opts' ); }

      const url = `${this.url}/${portal}/${id}/booking-confirmation`;

      const toSave = new AccountBookingConfirmation({
         replyTo: abc?.replyTo,
         parties: abc.parties.map( p => ({
            recipientType: p?.recipientType,
            email: p?.currentUser ? 'user' : p.email,
            brand: p?.brand,
         })),
      });

      return this.http.post<ResponseWrapper<boolean>>( url, toSave, { params: { useLambda: true } } ).pipe(
         map( rw => rw.response.collection[0] ),
         tap( _ => this.cache.clearSet( this.url )),
         catchError( e => { throw new Error( e ); }),
      );
   }

   reset() {
      this.cache.clearSet( this.url );
      this.refresh$.next();
   }
}
